/** @file Rotate drag: each bone's world X direction turns by the swept angle, exactly under any parent. */
import { effectiveParent } from '@pose2d/core/pose/effectiveParent';
import type { InheritMode } from '@pose2d/core/types';
import { dragModifiers } from '@pose2d/editor-geometry/dragModifiers';
import { dragRotate } from '@pose2d/editor-geometry/dragRotate';
import { gizmoHandles } from '@pose2d/editor-geometry/gizmoHandles';
import { parentSpace } from '@pose2d/editor-geometry/parentSpace';
import { snapAngle } from '@pose2d/editor-geometry/snapAngle';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Id } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import type { PreviewValues } from '../../panels/viewport/scene/channelOffset';
import { localValue } from '../../panels/viewport/scene/localValue';
import type { ToolContext, ToolGesture } from '../../panels/viewport/viewportTypes';
import { worldAt } from '../../panels/viewport/worldAt';
import { boneOrigin } from './boneOrigin';
import { parentWorldOf } from './parentWorldOf';
import type { GestureStart } from './transformPress';
import { transformCommands } from './transformCommands';

interface Start { eff: Affine | null; worldDeg: number; shearX: number; local: number }

/** The bone's own direction (local rotation + shearX) through its effective parent, as parentSpace.rotation inverts it. */
function startOf(ctx: ToolContext, i: number): Start {
  const local = localValue(ctx.pose, i, 'rotation');
  const shearX = localValue(ctx.pose, i, 'shearX');
  const parent = parentWorldOf(ctx.pose, ctx.data, i);
  const eff = parent === null ? null : effectiveParent(new Float64Array(6), parent, (ctx.pose.inherit[i] ?? 0) as InheritMode);
  const r = angle.degToRad(local + shearX);
  const d = eff === null ? [trig.cos(r), trig.sin(r)] : affine.applyVec(eff, trig.cos(r), trig.sin(r), [0, 0]);
  return { eff, worldDeg: angle.radToDeg(trig.atan2(d[1] ?? 0, d[0] ?? 1)), shearX, local };
}

/**
 * Shift snaps the swept angle to 15 degrees; the ring stays on the gizmo bone. Each local value is unwrapped next to
 * the previous preview value (a move steps under a half turn), so a drag past a half turn keys the turn it swept
 * under any parent, reflected included (R-E1-06: the drag value is continuous).
 */
export function rotateGesture({ ctx, edit, bones, press }: GestureStart): ToolGesture {
  const pivot = boneOrigin(ctx.pose, bones.at(-1) ?? 0);
  const starts = bones.map((i) => startOf(ctx, i));
  const prev = starts.map((s) => s.local);
  let drag = { origin: pivot, last: worldAt(ctx, press), total: 0 };
  let values = new Map<Id, PreviewValues>();
  return {
    move(p) {
      drag = dragRotate(drag, worldAt(ctx, p));
      const deg = dragModifiers(ctx.snap, p).constrain ? snapAngle(drag.total) : drag.total;
      values = new Map(bones.map((i, k) => {
        const s = starts[k] ?? { eff: null, worldDeg: 0, shearX: 0, local: 0 };
        const rotation = parentSpace.rotation(s.eff, s.worldDeg + deg, s.shearX, prev[k] ?? s.local);
        prev[k] = rotation;
        return [ctx.maps.boneIds[i] ?? '', { rotation }];
      }));
      ctx.setPreview(values);
      const centre = viewCamera.worldToScreen(ctx.camera, pivot[0], pivot[1], [0, 0]);
      ctx.setGizmo({ handles: gizmoHandles('rotate', { camera: ctx.camera, origin: pivot, rotation: 0 }), active: 'ring', points: [], lines: [[centre, [p.sx, p.sy]]] });
    },
    release: () => transformCommands(edit, values),
    cancel() {},
  };
}
